<template>
    <ToggleSimple v-if="facetsData" cssClass="toggle-type-2" :toggleable="config.toggleable === false ? false : true" :default="config.toggleable === false ? true : config.default || false">
        <template #toggle>
            <div class="left">
                <div class="toggle-text">
                    {{ $t(`components.dataset_specification.${config.slug}.name`) }}
                </div>
                <div class="toggle-description">
                    {{ $t(`components.dataset_specification.${config.slug}.description`) }}
                </div>
            </div>
            <div class="right">
                <div class="toggle-icon"></div>
            </div>
        </template>

        <template #content>
            <template v-if="displayBuckets.length">
                <component 
                    :is="displayComponent"
                    :buckets="config.type !== 'graph' ? displayBuckets : []"
                    :values="config.type === 'graph' ? displayBuckets : []"
                    :slug="config.slug"
                />
            </template>
        </template>
    </ToggleSimple>
</template>

<script>
    import { mapState } from 'pinia';
    import { useDatasetStore } from '@/stores/dataset';
    import ToggleSimple from '@/components/elements/misc/ToggleSimple';
    import FacetsKpi from '@/components/builder/specification/FacetsKpi';
    import FacetsList from '@/components/builder/specification/FacetsList';
    import FacetsSbi from '@/components/builder/specification/FacetsSbi';
    import { employeesRangeMap, revenueRangeMap } from '@/utils/maps/rangeMap';

    export default {
        name: 'SpecificationFacets',
        components: {
            ToggleSimple,
            FacetsKpi,
            FacetsList,
            FacetsSbi
        },
        props: {
            config: {
                type: Object,
                required: true
            }
        },
        computed: {
            ...mapState(useDatasetStore, ['dataset', 'facets']),
            facetsData() {
                return this.facets?.[this.config.dataSource]?.buckets;
            },
            displayComponent() {
                switch (this.config.type) {
                    case 'kpi':
                        return 'FacetsKpi';
                    case 'sbi':
                        return 'FacetsSbi';
                    case 'list':
                    default:
                        return 'FacetsList';
                }
            },
            displayBuckets() {
                if (!this.facetsData) return [];
                const buckets = [...this.facetsData];
                const bucketsMap = this.createBucketsMap(buckets);
                let mappedBuckets = this.applyMapping(buckets);
                
                if (this.config.dataSource === 'employees_range') {
                    mappedBuckets = this.applyRangeMapping(mappedBuckets, employeesRangeMap);
                } else if (this.config.dataSource === 'revenue_range') {
                    mappedBuckets = this.applyRangeMapping(mappedBuckets, revenueRangeMap);
                }
                
                return this.sortBuckets(mappedBuckets, bucketsMap);
            }
        },
        methods: {
            createBucketsMap(buckets) {
                const map = {};
                buckets.forEach(bucket => {
                    map[String(bucket.val).toLowerCase()] = bucket;
                });
                return map;
            },
            applyMapping(buckets) {
                if (!this.config.map) return buckets;
                
                return buckets.map(item => {
                    const mapEntry = this.config.map.find(mapItem => 
                        String(mapItem.value).toLowerCase() === String(item.val).toLowerCase()
                    );
                    return {
                        ...item,
                        displayName: mapEntry?.name || item.val,
                        icon: mapEntry?.icon || null,
                        color: mapEntry?.color || null
                    };
                });
            },
            applyRangeMapping(buckets, rangeMap) {
                return buckets.map(item => {
                    const rangeText = rangeMap[item.val];
                    if (rangeText) {
                        return {
                            ...item,
                            displayName: rangeText,
                            text: rangeText
                        };
                    }
                    return item;
                });
            },
            sortBuckets(buckets, bucketsMap) {
                if (this.config.values && this.config.values.length) {
                    return this.sortByValues(buckets);
                } 
                
                if (this.config.map && !this.config.order) {
                    return this.sortByMapOrder(bucketsMap);
                }
                
                if (this.config.order) {
                    return this.sortByOrderType(buckets);
                }
                return buckets;
            },
            sortByValues(buckets) {
                return this.getOrderedFacets(buckets, this.config.values);
            },
            sortByMapOrder(bucketsMap) {
                const mappedBuckets = [];

                if (!this.config.map) return Object.values(bucketsMap);
                
                this.config.map.forEach(mapItem => {
                    const lowerValue = String(mapItem.value).toLowerCase();
                    if (bucketsMap[lowerValue]) {
                        const bucket = {...bucketsMap[lowerValue]};
                        bucket.displayName = mapItem.name || bucket.val;
                        bucket.icon = mapItem.icon || null;
                        bucket.color = mapItem.color || null;
                        mappedBuckets.push(bucket);
                    }
                });
                return mappedBuckets;
            },
            sortByOrderType(buckets) {
                if (this.config.order === 'alphabetical') {
                    return [...buckets].sort((a, b) => {
                        const nameA = (a.displayName || a.val).toLowerCase();
                        const nameB = (b.displayName || b.val).toLowerCase();
                        return nameA.localeCompare(nameB);
                    });
                }
                
                if (this.config.order === 'count') {
                    return [...buckets].sort((a, b) => b.count - a.count);
                }

                if (this.config.order === 'numerical') {
                    return [...buckets].sort((a, b) => {
                        const numA = Number(a.val);
                        const numB = Number(b.val);
                        return numA - numB;
                    });
                }
                
                return buckets;
            },
            getOrderedFacets(buckets, orderedKeys) {
                const bucketMap = {};
                
                buckets.forEach(item => {
                    bucketMap[String(item.val).toLowerCase()] = item;
                });
                
                return orderedKeys
                    .filter(key => bucketMap[String(key).toLowerCase()])
                    .map(key => bucketMap[String(key).toLowerCase()]);
            }
        }
    }
</script>